import PropTypes from 'prop-types';
import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { AuthGuard } from 'src/auth/guard';
import DashboardLayout from 'src/layouts/dashboard';
import RoleBasedGuard from 'src/auth/guard/role-based-guard';

import { RouteGuard } from 'src/components/permissions';
import { LoadingScreen } from 'src/components/loading-screen';

import { CheckListView } from 'src/sections/finance/checks/view';
import { JournalListView } from 'src/sections/finance/journal/view';

// ----------------------------------------------------------------------

const AccountCatalogPage = lazy(() => import('src/pages/medicore/finance/account-catalog-list'));
const PosViewPage = lazy(() => import('src/pages/medicore/pos/pos-page'));
const AccountingEntryPage = lazy(
  () => import('src/pages/medicore/finance/accounting-entry-add-view-page')
);
const FixedAssetsListPage = lazy(() => import('src/pages/medicore/finance/fixed-assets/fixed-assets-list-page'));
const FixedAssetNewPage = lazy(() => import('src/pages/medicore/finance/fixed-assets/fixed-asset-new-page'));
const FixedAssetEditPage = lazy(() => import('src/pages/medicore/finance/fixed-assets/fixed-asset-edit-page'));
const FixedAssetDetailPage = lazy(() => import('src/pages/medicore/finance/fixed-assets/fixed-asset-detail-page'));
const FixedAssetsDashboardPage = lazy(() => import('src/pages/medicore/finance/fixed-assets/fixed-assets-dashboard-page'));
const FixedAssetsDepreciationPage = lazy(() => import('src/pages/medicore/finance/fixed-assets/fixed-assets-depreciation-page'));
const FixedAssetsReportsPage = lazy(() => import('src/pages/medicore/finance/fixed-assets/fixed-assets-reports-page'));
const FixedAssetByCodePage = lazy(() => import('src/pages/medicore/finance/fixed-assets/fixed-asset-by-code-page'));
const InvoicesPage = lazy(() => import('src/pages/medicore/finance/invoices-view-list-page'));
const InvoicesFormPage = lazy(() => import('src/pages/medicore/finance/invoices-form-page'));
const LoansPage = lazy(() => import('src/pages/medicore/finance/loans-list-view-page'));
const PayrollPage = lazy(() => import('src/pages/medicore/finance/payroll-view-list-page'));
const PettyCashPage = lazy(() => import('src/pages/medicore/finance/petty-cash-list-view-page'));
// const ReportPage = lazy(() => import('src/pages/medicore/finance/report-view-page'));
const TrialBalance = lazy(()=> import('src/sections/finance/Trial-balance/trial-balance'))
const IncomeStatement = lazy(()=> import('src/sections/finance/income-statement/income-statement'))
const JournalEntryPage = lazy(() => import('src/pages/medicore/finance/journal-entry-page'));
const ExpensesInvoices = lazy(()=> import('src/pages/medicore/finance/Invoices-expenses-606'))
const SoldServicesReport607 = lazy(()=> import('src/pages/medicore/finance/Invoices-soldReport-607'))
const ElectronicInvoicingPage = lazy(() => import('src/pages/medicore/finance/electronic-invoicing-page'));
const ReceivedEcfListPage = lazy(() => import('src/pages/medicore/finance/received-ecf-list-page'));
const ReceivedEcfDetailPage = lazy(() => import('src/pages/medicore/finance/received-ecf-detail-page'));
const ReceivedAprobacionesListPage = lazy(() => import('src/pages/medicore/finance/received-aprobaciones-list-page'));
const ReceivedAprobacionesDetailPage = lazy(() => import('src/pages/medicore/finance/received-aprobaciones-detail-page'));
const AprobacionComercialPage = lazy(() => import('src/pages/medicore/finance/aprobacion-comercial-page'));

// ----------------------------------------------------------------------

/**
 * Componente wrapper para proteger rutas de finanzas con permisos específicos
 * ⚠️ ULTRA-RESTRICTIVO: Solo master ve todo, otros usuarios solo lo que tienen permisos
 */
const ProtectedRoute = ({ route, children }) => (
  <RouteGuard route={route} fallbackRoute="/medicore" showFallback>
    <Suspense fallback={<LoadingScreen />}>{children}</Suspense>
  </RouteGuard>
);

ProtectedRoute.propTypes = {
  route: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// ----------------------------------------------------------------------

export const medicoreFinanceRoutes = [
  {
    path: 'medicore/finance',
    element: (
      <AuthGuard>
        <RoleBasedGuard roles={['doctor', 'employee']}>
          <DashboardLayout>
            <Outlet />
          </DashboardLayout>
        </RoleBasedGuard>
      </AuthGuard>
    ),
    children: [
      {
        path: 'accounts',
        children: [
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/finance/accounts/list">
                <AccountCatalogPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'journal',
        children: [
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/finance/journal/list">
                <JournalListView />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'checks',
        children: [
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/finance/checks/list">
                <CheckListView />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'pos',
        element: (
          <ProtectedRoute route="/medicore/finance/pos">
            <PosViewPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'entry',
        children: [
          {
            element: (
              <ProtectedRoute route="/medicore/finance/entry/list">
                <JournalEntryPage />
              </ProtectedRoute>
            ),
            index: true,
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute route="/medicore/finance/entry/new">
                <AccountingEntryPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/finance/entry/list">
                <JournalEntryPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'fixed-assets',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute route="/medicore/finance/fixed-assets">
                <FixedAssetsListPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute route="/medicore/finance/fixed-assets/new">
                <FixedAssetNewPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'dashboard',
            element: (
              <ProtectedRoute route="/medicore/finance/fixed-assets/dashboard">
                <FixedAssetsDashboardPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'depreciation',
            element: (
              <ProtectedRoute route="/medicore/finance/fixed-assets/depreciation">
                <FixedAssetsDepreciationPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'reports',
            element: (
              <ProtectedRoute route="/medicore/finance/fixed-assets/reports">
                <FixedAssetsReportsPage />
              </ProtectedRoute>
            ),
          },
          {
            // Detalle del activo: destino del QR de la etiqueta. Va después de los segmentos
            // estáticos para que 'new', 'dashboard', etc. ganen el match.
            path: ':id',
            element: (
              <ProtectedRoute route="/medicore/finance/fixed-assets/details">
                <FixedAssetDetailPage />
              </ProtectedRoute>
            ),
          },
          {
            path: ':id/edit',
            element: (
              <ProtectedRoute route="/medicore/finance/fixed-assets/edit">
                <FixedAssetEditPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'loans',
        element: (
          <ProtectedRoute route="/medicore/finance/loans">
            <LoansPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'invoices',
        children: [
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/finance/invoices/list">
                <InvoicesPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'new',
            element: (
              <ProtectedRoute route="/medicore/finance/invoices/new">
                <InvoicesFormPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'edit/:id',
            element: (
              <ProtectedRoute route="/medicore/finance/invoices/list">
                <InvoicesFormPage />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: 'payroll',
        element: (
          <ProtectedRoute route="/medicore/finance/payroll">
            <PayrollPage />
          </ProtectedRoute>
        ),
      },
      {
        path: 'petty-cash',
        element: (
          <ProtectedRoute route="/medicore/finance/petty-cash">
            <PettyCashPage />
          </ProtectedRoute>
        ),
      },
      {
        path: "Expenses-purchases",
        children: 
        [
              {
                path: "listInvoice",
                element: 
                (
                  <ProtectedRoute route='/medicore/finance/Expenses-purchases/expenses/listInvoice'>
                    <ExpensesInvoices/>
                  </ProtectedRoute>
                )
              },
              {
                path: "soldInvoiceReport",
                element: (
                  <ProtectedRoute route='/medicore/finance/Expenses-purchases/expenses/soldInvoiceReport'>
                    <SoldServicesReport607/>
                  </ProtectedRoute>
                )
              }
        ]
      },
      {
        path: 'report',
        children: [
          { path: 'trialBalance',
            element: (
              <ProtectedRoute route="/medicore/finance/report/trialBalance">
                <TrialBalance />
              </ProtectedRoute>
            )
          },
          {
            path: 'incomeStatement',
            element: (
              <ProtectedRoute route="/medicore/finance/report/incomeStatement">
                <IncomeStatement />
              </ProtectedRoute>
            )
          }
        ]
      },
      {
        path: 'ecf',
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute route="/medicore/finance/ecf/list">
                <ElectronicInvoicingPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'list',
            element: (
              <ProtectedRoute route="/medicore/finance/ecf/list">
                <ElectronicInvoicingPage />
              </ProtectedRoute>
            ),
          },
          {
            path: 'received-ecf',
            children: [
              {
                index: true,
                element: (
                  <ProtectedRoute route="/medicore/finance/received-ecf">
                    <ReceivedEcfListPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: ':id',
                element: (
                  <ProtectedRoute route="/medicore/finance/received-ecf">
                    <ReceivedEcfDetailPage />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: 'received-aprobaciones',
            children: [
              {
                index: true,
                element: (
                  <ProtectedRoute route="/medicore/finance/received-aprobaciones">
                    <ReceivedAprobacionesListPage />
                  </ProtectedRoute>
                ),
              },
              {
                path: ':id',
                element: (
                  <ProtectedRoute route="/medicore/finance/received-aprobaciones">
                    <ReceivedAprobacionesDetailPage />
                  </ProtectedRoute>
                ),
              },
            ],
          },
          {
            path: 'aprobacion-comercial',
            element: (
              <ProtectedRoute route="/medicore/finance/ecf/aprobacion-comercial">
                <AprobacionComercialPage />
              </ProtectedRoute>
            ),
          },
          {
            // Deep-link to a single emitted e-CF; ElectronicInvoicingListView reads
            // :id via useParams and auto-opens the detail drawer for that invoice.
            // Declared last so static segments above ('list', 'received-ecf',
            // 'received-aprobaciones') take precedence on match.
            path: ':id',
            element: (
              <ProtectedRoute route="/medicore/finance/ecf/list">
                <ElectronicInvoicingPage />
              </ProtectedRoute>
            ),
          },
        ]
      }
    ],
  },
  {
    // Ruta corta del QR de etiqueta ('/af/:assetCode'), fuera de 'medicore/finance'
    // para que el código quepa en el ancho del código de barras. Reusa el permiso de
    // detalle de activos fijos: quien puede ver el detalle puede resolver el QR.
    path: 'af/:assetCode',
    element: (
      <AuthGuard>
        <RoleBasedGuard roles={['doctor', 'employee']}>
          <DashboardLayout>
            <ProtectedRoute route="/medicore/finance/fixed-assets/details">
              <FixedAssetByCodePage />
            </ProtectedRoute>
          </DashboardLayout>
        </RoleBasedGuard>
      </AuthGuard>
    ),
  },
];
